import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";
import useWhisperMic from "@/hooks/useWhisperMic";

/* ============================================================
 /charts/submit — Drop your track for The Charts.

 Flow:
 FORM → GENERATING (Suno renders ~60-120s) → PREVIEW (listen)
 └─ PUBLISH: track goes live on the Charts
 └─ REVISE: tweak lyrics / style / tempo / mood → regenerate
 (max 5 revisions per track to keep Suno costs sane)

 Writer-first rule: 3+ lines, 8+ unique words. No one-prompt slop.
 ============================================================ */

const PAID_TIERS = new Set(["battle_pass", "champion_pass"]);

const STYLE_OPTIONS = [
 // SONG-FIRST styles (Soniva-style — catchy, polished, storytelling)
 { v: "cinematic_pop", label: "CINEMATIC POP", sub: "anthemic, harmonies, cinematic" },
 { v: "inspirational_ballad", label: "BALLAD", sub: "piano, strings, big chorus" },
 { v: "singer_songwriter", label: "SINGER-SONGWRITER", sub: "acoustic, storytelling" },
 { v: "indie_anthem", label: "INDIE ANTHEM", sub: "gang vocals, singalong" },
 { v: "worship_gospel", label: "GOSPEL", sub: "organ, choir, big lift" },
 // CROSSOVER + VIBE styles (Feb 2026)
 { v: "reggae", label: "REGGAE", sub: "island one-drop, sunny" },
 { v: "ska_punk_reggae", label: "SKA PUNK", sub: "Sublime-style, surf punk" },
 { v: "sunny_alt", label: "SUNNY ALT", sub: "Weezer-vibe, beach-drive" },
 { v: "happy_hip_hop", label: "HAPPY HIP-HOP", sub: "feel-good, morning pump" },
 { v: "hick_hop", label: "HICK-HOP", sub: "country + rap, dirt-road" },
 { v: "alt_hop_trip", label: "ALT HOP", sub: "Gorillaz-vibe, trip-hop groove" },
 { v: "dark_viking", label: "DARK VIKING", sub: "nordic folk chants, war drums, metal" },
 // RAP-FIRST styles
 { v: "boom_bap", label: "BOOM BAP", sub: "90s soul sample" },
 { v: "aggressive", label: "AGGRESSIVE", sub: "hardcore, hard drums" },
 { v: "trap", label: "TRAP", sub: "808s, melodic hook" },
 { v: "drill", label: "DRILL", sub: "UK drill, dark 808s" },
 { v: "smooth", label: "SMOOTH", sub: "chill, laid-back" },
 { v: "conscious", label: "CONSCIOUS", sub: "storytelling, lyrical" },
 { v: "hip_hop", label: "HIP HOP", sub: "classic, funky drums" },
 { v: "pop", label: "POP", sub: "melodic, radio hook" },
 { v: "rnb", label: "R&B", sub: "smooth soulful groove" },
 { v: "edm", label: "EDM", sub: "festival drops" },
 { v: "country", label: "COUNTRY", sub: "acoustic, twangy hook" },
 { v: "latin", label: "LATIN", sub: "reggaeton, spicy hook" },
 { v: "rock", label: "ROCK", sub: "distorted guitars" },
];

const TEMPO_OPTIONS = [
 { v: "slow", label: "SLOW", sub: "~80 BPM" },
 { v: "med", label: "MEDIUM", sub: "~100 BPM" },
 { v: "fast", label: "FAST", sub: "~140 BPM" },
];

const MOOD_OPTIONS = [
 { v: "chill", label: "CHILL", sub: "relaxed" },
 { v: "hype", label: "HYPE", sub: "high energy" },
 { v: "emotional", label: "EMOTIONAL", sub: "heartfelt" },
 { v: "dark", label: "DARK", sub: "moody, ominous" },
 { v: "triumphant", label: "TRIUMPHANT", sub: "victory anthem" },
];

const VOICE_OPTIONS = [
 { v: "female_ethereal", label: "FEM · ETHEREAL", sub: "cinematic, harmonies (Soniva-style)" },
 { v: "male_britpop_laconic", label: "MALE · LACONIC", sub: "Gorillaz-vibe, laid-back Brit nasal" },
 { v: "male_singer", label: "MALE · SINGER", sub: "warm, breathy ballad" },
 { v: "male_deep", label: "MALE · DEEP", sub: "gravelly baritone" },
 { v: "male_gruff", label: "MALE · GRUFF", sub: "hard country" },
 { v: "male_young", label: "MALE · YOUNG", sub: "chirpy, sharp" },
 { v: "male_trap", label: "MALE · TRAP", sub: "autotuned melodic" },
 { v: "male_og", label: "MALE · OG", sub: "gangster rap veteran" },
 { v: "female_pop", label: "FEM · POP", sub: "bright, catchy" },
 { v: "female_rap", label: "FEM · RAP", sub: "fierce, sharp flow" },
 { v: "female_soul", label: "FEM · SOUL", sub: "smooth R&B" },
 { v: "female_country", label: "FEM · COUNTRY", sub: "twangy sweet" },
 { v: "viking_male", label: "VIKING · MALE", sub: "raw raspy warrior" },
 { v: "viking_female", label: "VIKING · FEMALE", sub: "haunting, powerful" },
 { v: "viking_duet", label: "VIKING · DUET", sub: "man + woman trading lines" },
 { v: "viking_sisters", label: "VIKING · SISTERS", sub: "two women, low + high" },
 { v: "nordic_ritual", label: "NORDIC · RITUAL", sub: "hypnotic whisper to raw cry" },
];

export default function TrackSubmit() {
 const { user } = useAuth();
 const nav = useNavigate();

 const [title, setTitle] = useState("");
 const [lyrics, setLyrics] = useState("");
 const [style, setStyle] = useState("cinematic_pop");
 const [tempo, setTempo] = useState("med");
 const [mood, setMood] = useState("emotional");
 const [voice, setVoice] = useState("female_ethereal");
 const [vibe, setVibe] = useState("");
 const [autoArrange, setAutoArrange] = useState(true);
 const [alias, setAlias] = useState("");
 const [anonymous, setAnonymous] = useState(false);
 const [explicit, setExplicit] = useState(false);

 const [busy, setBusy] = useState(false);
 const [err, setErr] = useState("");
 const [trackId, setTrackId] = useState(null);
 const [phase, setPhase] = useState("form"); // form → generating → preview → published → failed
 const [audioUrl, setAudioUrl] = useState(null);
 const [revisionsLeft, setRevisionsLeft] = useState(5);
 const [sunoState, setSunoState] = useState(null);
 const [ageSec, setAgeSec] = useState(0);

 // ─── DRAFT AUTOSAVE (Sept 17 2026 — the "lost reply song" fix) ────
 // Paul wrote a heartfelt reply to Second Cousin, put his phone in
 // his pocket, came back, and the whole thing was gone because React
 // unmounted the form when he swiped away. Never again. TWO layers:
 //   • localStorage — instant, per-device, 99% of the pain
 //   • backend /api/tracks/draft — cross-device, survives cache clear
 // Restore on mount prefers whichever is newer. Clear on publish.
 const DRAFT_KEY = "yt.charts.submit.draft.v1";
 const [draftSavedAt, setDraftSavedAt] = useState(null);
 const [draftRestored, setDraftRestored] = useState(false);
 const [draftServerHydrated, setDraftServerHydrated] = useState(false);

 useEffect(() => {
   // Layer 1: instant restore from localStorage
   let localDraft = null;
   try {
     const raw = localStorage.getItem(DRAFT_KEY);
     if (raw) localDraft = JSON.parse(raw);
   } catch (_) {}

   // Layer 2: ask the server (may have a newer version from another device)
   (async () => {
     let serverDraft = null;
     try {
       const { data } = await axios.get(`${API}/tracks/draft`);
       serverDraft = data?.draft || null;
     } catch (_) {}

     // Merge — whichever is newer wins
     const pickServer = serverDraft && (
       !localDraft ||
       (serverDraft.updated_at || "") > (String(localDraft.savedAt || "") ? new Date(localDraft.savedAt).toISOString() : "")
     );
     const d = pickServer ? serverDraft : localDraft;
     if (!d) { setDraftServerHydrated(true); return; }

     if (d.title) setTitle(d.title);
     if (d.lyrics) setLyrics(d.lyrics);
     if (d.style) setStyle(d.style);
     if (d.tempo) setTempo(d.tempo);
     if (d.mood) setMood(d.mood);
     if (d.voice) setVoice(d.voice);
     if (d.vibe) setVibe(d.vibe);
     if (typeof d.auto_arrange === "boolean") setAutoArrange(d.auto_arrange);
     if (typeof d.autoArrange === "boolean") setAutoArrange(d.autoArrange);
     if (d.alias) setAlias(d.alias);
     if (typeof d.anonymous === "boolean") setAnonymous(d.anonymous);
     if (typeof d.explicit === "boolean") setExplicit(d.explicit);
     if (d.title || d.lyrics || d.vibe) setDraftRestored(true);
     setDraftServerHydrated(true);
   })();
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 // Fast local save on every keystroke (< 1ms, no network)
 useEffect(() => {
   if (phase !== "form") return;
   try {
     localStorage.setItem(DRAFT_KEY, JSON.stringify({
       title, lyrics, style, tempo, mood, voice, vibe,
       autoArrange, alias, anonymous, explicit,
       savedAt: Date.now(),
     }));
     setDraftSavedAt(Date.now());
   } catch (_) {}
 }, [title, lyrics, style, tempo, mood, voice, vibe,
     autoArrange, alias, anonymous, explicit, phase]);

 // Debounced backend save — every 3s of stillness push to server.
 // Only after the initial hydrate so we don't overwrite server with
 // empty form fields on first mount.
 useEffect(() => {
   if (phase !== "form" || !draftServerHydrated) return;
   const timer = setTimeout(async () => {
     try {
       await axios.put(`${API}/tracks/draft`, {
         title, lyrics, style, tempo, mood, voice, vibe,
         auto_arrange: autoArrange, alias, anonymous, explicit,
       });
     } catch (_) {}
   }, 3000);
   return () => clearTimeout(timer);
 }, [title, lyrics, style, tempo, mood, voice, vibe,
     autoArrange, alias, anonymous, explicit, phase, draftServerHydrated]);

 // Clear both layers after a successful publish
 useEffect(() => {
   if (phase === "published") {
     try { localStorage.removeItem(DRAFT_KEY); } catch (_) {}
     axios.delete(`${API}/tracks/draft`).catch(() => {});
   }
 }, [phase]);

 // ─────────────────────────────────────────────────────────────────

 // Poll while Suno renders. Declared before early returns.
 useEffect(() => {
 if (!trackId || phase !== "generating") return;
 let cancelled = false;
 const poll = async () => {
 try {
 const { data } = await axios.get(`${API}/tracks/status/${trackId}`);
 if (cancelled) return;
 if (data.status === "preview" || data.status === "ready") {
 setAudioUrl(data.audio_url);
 setPhase("preview");
 } else if (data.status === "failed") {
 setPhase("failed");
 setErr(data.error || "Track generation failed. Tweak lyrics or style and try again.");
 } else {
 // Still generating — surface Suno's live state so users see progress
 // instead of a frozen "Recording..." spinner.
 if (data.suno_state) setSunoState(data.suno_state);
 if (typeof data.age_sec === "number") setAgeSec(data.age_sec);
 }
 } catch (e) {}
 };
 const iv = setInterval(poll, 6000);
 poll();
 return () => { cancelled = true; clearInterval(iv); };
 }, [trackId, phase]);

 // Access gates
 if (!user) {
 return (
 <div className="max-w-3xl mx-auto px-6 py-24 text-center">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-4"> SIGN IN REQUIRED</div>
 <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-6">
 DROP A <span className="text-yt-red">TRACK</span>
 </h1>
 <Link to="/register?next=/charts/submit" data-testid="track-signup-cta"
 className="inline-block bg-yt-yellow text-black font-display text-xl px-8 py-4 tracking-wider shadow-brutal">
 CREATE ACCOUNT
 </Link>
 <div className="mt-4 font-mono text-xs">
 Already have one? <Link to="/login?next=/charts/submit" className="text-yt-yellow underline">SIGN IN</Link>
 </div>
 </div>
 );
 }

 const isPaid = user.role === "admin" || PAID_TIERS.has(user.tier);
 if (!isPaid) {
 return (
 <div className="max-w-3xl mx-auto px-6 py-20">
 <div className="border-4 border-yt-red bg-yt-surface shadow-brutal p-8 md:p-10 text-center">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-4"> PAID FEATURE</div>
 <h1 className="font-display text-4xl md:text-6xl tracking-tight mb-4">
 DROP TO THE <span className="text-yt-yellow">CHARTS</span>
 </h1>
 <p className="font-body text-white/80 mb-2">
 Publishing songs to the Charts is included with the <span className="text-yt-yellow font-bold">Arena + Charts Pass ($4.99/mo)</span>.
 </p>
 <p className="font-mono text-xs text-yt-muted mb-6">
 One pass unlocks battles AND unlimited song drops. Listening to other people's songs is always free.
 </p>
 <Link to="/subscribe" data-testid="track-paywall-cta"
 className="inline-block bg-yt-yellow text-black font-display text-xl tracking-wider px-8 py-4 shadow-brutal">
 ▶ GET A PASS
 </Link>
 </div>
 </div>
 );
 }

 const submit = async () => {
 setErr("");
 if (title.trim().length < 2) return setErr("Add a title (2+ characters).");
 if (lyrics.trim().length < 40) return setErr("Writer-first: lyrics need at least 40 characters and 8+ unique words. Paragraph style is fine — the AI will structure it into a song.");
 setBusy(true);
 try {
 const { data } = await axios.post(`${API}/tracks/submit`, {
 title: title.trim(), lyrics: lyrics.trim(),
 style, tempo, mood, voice,
 vibe: vibe.trim() || null,
 alias: alias.trim() || null, anonymous,
 auto_arrange: autoArrange,
 explicit,
 });
 setTrackId(data.track_id);
 setPhase("generating");
 setAudioUrl(null);
 } catch (e) {
 setErr(e.response?.data?.detail || "Submission failed");
 } finally { setBusy(false); }
 };

 const revise = async () => {
 setErr("");
 setBusy(true);
 try {
 const { data } = await axios.post(`${API}/tracks/${trackId}/revise`, {
 title: title.trim(), lyrics: lyrics.trim(),
 style, tempo, mood, voice,
 vibe: vibe.trim() || null,
 });
 setRevisionsLeft(data.revisions_left);
 setPhase("generating");
 setAudioUrl(null);
 } catch (e) {
 setErr(e.response?.data?.detail || "Revise failed");
 } finally { setBusy(false); }
 };

 const publish = async () => {
 setErr("");
 setBusy(true);
 try {
 await axios.post(`${API}/tracks/${trackId}/publish`);
 setPhase("published");
 } catch (e) {
 setErr(e.response?.data?.detail || "Publish failed");
 } finally { setBusy(false); }
 };

 // === GENERATING SCREEN ===
 if (phase === "generating") {
 // Human-friendly labels for Suno's internal states, so users see progress
 // instead of a frozen spinner.
 const STATE_MSG = {
   PENDING: "Suno is queuing your track...",
   TEXT_SUCCESS: "Suno wrote the lyrics — now composing the music...",
   FIRST_SUCCESS: "First mix is done — polishing the final version...",
 };
 const stateLabel = STATE_MSG[sunoState] || "Suno is composing your track...";
 const mins = Math.floor(ageSec / 60), secs = ageSec % 60;
 return (
 <div className="max-w-3xl mx-auto px-6 md:px-10 py-16">
 <div className="border-4 border-yt-yellow bg-yt-surface shadow-brutal p-8 md:p-10">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-4 animate-pulse" data-testid="track-generating-label">IN THE STUDIO</div>
 <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-4" data-testid="track-generating-title">{stateLabel}</h2>
 <p className="font-body text-white/70 mb-4">
 Full-song generation takes 90-180 seconds on Suno V5. Do not close this tab — we'll load the preview when it's ready.
 </p>
 {ageSec > 0 && (
   <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-4" data-testid="track-generating-elapsed">
     ELAPSED: {mins > 0 ? `${mins}m ` : ""}{secs}s · TIMEOUT AT 5:00
   </div>
 )}
 <div className="h-2 bg-yt-border overflow-hidden">
 <div className="h-full bg-yt-yellow animate-pulse" style={{ width: `${Math.min(95, 20 + ageSec / 3)}%` }} />
 </div>
 {revisionsLeft < 5 && (
 <div className="mt-6 font-mono text-[10px] tracking-widest text-yt-muted">
 REVISIONS LEFT: {revisionsLeft} / 5
 </div>
 )}
 </div>
 </div>
 );
 }

 // === PREVIEW SCREEN (listen before publishing) ===
 if (phase === "preview") {
 const copyLyrics = async () => {
 try { await navigator.clipboard.writeText(lyrics); } catch (e) {}
 };
 const startFreshWithLyrics = () => {
 // Keep the lyrics, wipe everything else. Fresh track_id will be
 // created on next submit, and revision counter resets to 5.
 setTrackId(null); setAudioUrl(null);
 setTitle(""); setStyle("boom_bap"); setTempo("med"); setMood("hype");
 setRevisionsLeft(5); setPhase("form"); setErr("");
 };
 return (
 <div className="max-w-3xl mx-auto px-6 md:px-10 py-16">
 <div className="border-4 border-yt-yellow bg-yt-surface shadow-brutal p-6 md:p-10">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-3"> PREVIEW · NOT ON CHARTS YET</div>
 <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-2">"{title.toUpperCase()}"</h2>
 <div className="font-mono text-xs text-yt-muted mb-6">
 {STYLE_OPTIONS.find(s => s.v === style)?.label} · {TEMPO_OPTIONS.find(t => t.v === tempo)?.label} · {MOOD_OPTIONS.find(m => m.v === mood)?.label}
 </div>

 <audio data-testid="preview-audio" src={audioUrl} controls autoPlay className="w-full mb-6" />

 {/* Publish + Revise primary actions */}
 <div className="flex gap-3 flex-wrap mb-6">
 <button data-testid="preview-publish" onClick={publish} disabled={busy}
 className="bg-yt-judge text-black font-display text-lg md:text-xl tracking-widest px-6 py-4 shadow-brutal hover:bg-white disabled:opacity-50 flex-1">
 ✓ PUBLISH TO CHARTS
 </button>
 <button data-testid="preview-revise" onClick={() => setPhase("revise")}
 disabled={busy || revisionsLeft <= 0}
 className="border-2 border-yt-red text-yt-red font-display text-lg md:text-xl tracking-widest px-6 py-4 hover:bg-yt-red hover:text-white disabled:opacity-50 flex-1">
 REVISE ({revisionsLeft} LEFT)
 </button>
 </div>

 {/* Lyrics reveal + reuse — for when the song didn't hit but the words are gold */}
 <div className="border-2 border-yt-border p-4 mb-4">
 <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
 <div className="font-mono text-[10px] tracking-[0.4em] text-yt-yellow"> YOUR LYRICS</div>
 <div className="flex gap-2">
 <button data-testid="preview-copy-lyrics" onClick={copyLyrics}
 className="font-mono text-[10px] tracking-widest border-2 border-yt-yellow text-yt-yellow px-3 py-1 hover:bg-yt-yellow hover:text-black transition-colors">
 COPY LYRICS
 </button>
 <button data-testid="preview-new-with-lyrics" onClick={startFreshWithLyrics}
 className="font-mono text-[10px] tracking-widest border-2 border-white text-white px-3 py-1 hover:bg-white hover:text-black transition-colors">
 NEW TRACK, SAME LYRICS
 </button>
 </div>
 </div>
 <pre className="font-mono text-xs md:text-sm text-white/85 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">{lyrics}</pre>
 </div>

 <div className="font-mono text-[10px] text-yt-muted italic">
 <strong>PUBLISH</strong> sends this to the public Charts (votes + comments preserved). <strong>REVISE</strong> regenerates with the same track ID (1 Suno credit). <strong>NEW TRACK, SAME LYRICS</strong> keeps your bars but starts a fresh track with 5 fresh revisions — perfect for trying a totally different genre.
 </div>

 {err && <div className="mt-4 border-l-4 border-yt-red bg-yt-red/10 p-3 font-mono text-sm text-yt-red">{err}</div>}
 </div>
 </div>
 );
 }

 // === REVISE FORM (pre-filled, user tweaks anything, regenerates) ===
 if (phase === "revise") {
 return (
 <FormBody
 heading="REVISE YOUR TRACK"
 subHead={`Tweak anything · ${revisionsLeft} revision${revisionsLeft === 1 ? "" : "s"} left`}
 title={title} setTitle={setTitle}
 lyrics={lyrics} setLyrics={setLyrics}
 style={style} setStyle={setStyle}
 tempo={tempo} setTempo={setTempo}
 mood={mood} setMood={setMood}
 voice={voice} setVoice={setVoice}
 vibe={vibe} setVibe={setVibe}
autoArrange={autoArrange} setAutoArrange={setAutoArrange}
 alias={alias} setAlias={setAlias}
 anonymous={anonymous} setAnonymous={setAnonymous}
 explicit={explicit} setExplicit={setExplicit}
 user={user}
 err={err}
 busy={busy}
 primaryCta="REGENERATE WITH THESE CHANGES"
 primaryTestId="revise-regenerate"
 onSubmit={revise}
 secondaryCta="← BACK TO PREVIEW"
 onSecondary={() => setPhase("preview")}
 showAliasBlock={false}
 />
 );
 }

 // === PUBLISHED (success screen) ===
 if (phase === "published") {
 return (
 <div className="max-w-3xl mx-auto px-6 md:px-10 py-16">
 <div className="border-4 border-yt-judge bg-yt-surface shadow-brutal p-8 md:p-10">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-judge mb-4">✓ LIVE ON THE CHARTS</div>
 <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-4">YOU'RE ON THE WALL </h2>
 <audio data-testid="published-audio" src={audioUrl} controls className="w-full mb-6" />
 <div className="flex gap-3 flex-wrap">
 <Link to={`/tracks/${trackId}`} data-testid="published-view-track"
 className="bg-yt-yellow text-black font-display text-lg tracking-widest px-5 py-3 shadow-brutal">
 ▶ VIEW MY TRACK
 </Link>
 <Link to="/charts" data-testid="published-view-charts"
 className="border-2 border-yt-yellow text-yt-yellow font-display text-lg tracking-widest px-5 py-3 hover:bg-yt-yellow hover:text-black">
 THE CHARTS
 </Link>
 <Link to="/" data-testid="published-home"
 className="border-2 border-white text-white font-display text-lg tracking-widest px-5 py-3 hover:bg-white hover:text-black">
 HOMEPAGE
 </Link>
 <button data-testid="published-drop-another"
 onClick={() => { setTrackId(null); setAudioUrl(null); setTitle(""); setLyrics(""); setPhase("form"); setRevisionsLeft(5); }}
 className="border-2 border-yt-red text-yt-red font-display text-lg tracking-widest px-5 py-3 hover:bg-yt-red hover:text-white">
 DROP ANOTHER
 </button>
 </div>
 </div>
 </div>
 );
 }

 // === FAILED ===
 if (phase === "failed") {
 return (
 <div className="max-w-3xl mx-auto px-6 md:px-10 py-16">
 <div className="border-4 border-yt-red bg-yt-surface shadow-brutal p-8 md:p-10">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-4">✕ GENERATION FAILED</div>
 <h2 className="font-display text-4xl mb-4">TRACK COULDN'T RENDER</h2>
 <p className="font-body text-white/70 mb-6">{err}</p>
 <button onClick={() => { setPhase("form"); setErr(""); setTrackId(null); }}
 className="bg-yt-yellow text-black font-display text-lg tracking-widest px-5 py-3 shadow-brutal">
 ▶ TRY AGAIN
 </button>
 </div>
 </div>
 );
 }

 // === INITIAL FORM ===
 return (
 <FormBody
 heading={<>SPIT. <span className="text-yt-red">DROP.</span> <span className="text-yt-yellow">CROWN.</span></>}
 subHead="BRING YOUR OWN LYRICS · SUNO V4.5 VOCALIZES · PREVIEW BEFORE PUBLISH"
 title={title} setTitle={setTitle}
 lyrics={lyrics} setLyrics={setLyrics}
 style={style} setStyle={setStyle}
 tempo={tempo} setTempo={setTempo}
 mood={mood} setMood={setMood}
 voice={voice} setVoice={setVoice}
 vibe={vibe} setVibe={setVibe}
autoArrange={autoArrange} setAutoArrange={setAutoArrange}
 alias={alias} setAlias={setAlias}
 anonymous={anonymous} setAnonymous={setAnonymous}
 explicit={explicit} setExplicit={setExplicit}
 user={user}
 err={err}
 busy={busy}
 primaryCta="▶ GENERATE PREVIEW"
 primaryTestId="track-submit-btn"
 onSubmit={submit}
 secondaryCta="← BACK TO CHARTS"
 onSecondary={() => nav("/charts")}
 showAliasBlock={true}
 draftRestored={draftRestored}
 setDraftRestored={setDraftRestored}
 draftSavedAt={draftSavedAt}
 onClearDraft={() => {
   try { localStorage.removeItem(DRAFT_KEY); } catch (_) {}
   setTitle(""); setLyrics(""); setVibe(""); setAlias("");
   setDraftRestored(false); setDraftSavedAt(null);
 }}
 />
 );
}

/* ============================================================
 Shared form body — used for both the initial submit AND the
 revise flow. Same inputs, different heading + primary CTA.
 ============================================================ */
function FormBody({
 heading, subHead,
 title, setTitle, lyrics, setLyrics,
 style, setStyle, tempo, setTempo, mood, setMood, voice, setVoice,
 vibe, setVibe,
 autoArrange, setAutoArrange,
 alias, setAlias, anonymous, setAnonymous,
 explicit, setExplicit,
 user, err, busy,
 primaryCta, primaryTestId, onSubmit,
 secondaryCta, onSecondary, showAliasBlock,
 draftRestored, setDraftRestored, draftSavedAt, onClearDraft,
}) {
 // Voice input for the lyrics textarea. Whisper backend transcribes speech
 // into text and appends to whatever's already in the box. Massive win for
 // Paul (TBI) and any user who'd rather sing/speak than type.
 const mic = useWhisperMic((txt) => setLyrics(v => (v ? v + " " : "") + txt));

 // DJ NEO — songwriter co-pilot lives in this same form so he can read
 // the current draft. Collapsed by default; expands on demand.
 const draftCtx = { title, lyrics, style, tempo, mood, voice, vibe };
 return (
 <div className="max-w-3xl mx-auto px-6 md:px-10 py-12">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2"> THE CHARTS</div>
 <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.9] mb-2">
 {heading}
 </h1>
 <p className="text-yt-muted font-mono text-xs tracking-widest mb-8 max-w-xl">{subHead}</p>

 {/* DRAFT RESTORED banner — surfaces the autosave the first time it
     restores content on a page load, so users know their work
     survived. Dismissible; also has a nuke button to start fresh. */}
 {draftRestored && (
 <div
   data-testid="draft-restored-banner"
   className="mb-6 border-2 border-yt-yellow bg-yt-yellow/10 p-4 flex items-start justify-between gap-3 flex-wrap"
 >
   <div className="flex-1 min-w-[200px]">
     <div className="font-mono text-[10px] tracking-[0.4em] text-yt-yellow mb-1">✓ DRAFT RESTORED</div>
     <div className="font-mono text-xs text-white/80 leading-relaxed">
       Picked up where you left off. Every keystroke autosaves to this device — swipe away, close the tab, roll the tractor — your words survive.
     </div>
   </div>
   <div className="flex gap-2 flex-wrap">
     <button
       type="button"
       data-testid="draft-keep-btn"
       onClick={() => setDraftRestored(false)}
       className="font-mono text-[10px] tracking-widest border-2 border-yt-yellow text-yt-yellow px-3 py-2 hover:bg-yt-yellow hover:text-black"
     >
       KEEP
     </button>
     {onClearDraft && (
       <button
         type="button"
         data-testid="draft-clear-btn"
         onClick={() => { if (window.confirm("Clear the saved draft and start fresh?")) onClearDraft(); }}
         className="font-mono text-[10px] tracking-widest border-2 border-yt-red text-yt-red px-3 py-2 hover:bg-yt-red hover:text-white"
       >
         START FRESH
       </button>
     )}
   </div>
 </div>
 )}

 <div className="space-y-6">
 <Field label="TRACK TITLE">
 <input data-testid="track-title-input"
 value={title} onChange={e => setTitle(e.target.value)} maxLength={80}
 placeholder="e.g. Northern Lights"
 className="w-full bg-yt-surface border-2 border-yt-border focus:border-yt-yellow px-4 py-3 font-body text-lg outline-none" />
 </Field>

 <Field label={<>YOUR LYRICS <span className="text-yt-muted">(40+ chars · 8+ unique words · paragraph style works)</span></>}>
 <div className="relative">
 <textarea data-testid="track-lyrics-input"
 value={lyrics} onChange={e => setLyrics(e.target.value)} maxLength={2000} rows={10}
 placeholder={"Just write — or tap the mic and speak your bars.\n\nStarted from the field but I'm building tools, brain barely wired but my brain's my jewels..."}
 className="w-full bg-yt-surface border-2 border-yt-border focus:border-yt-yellow px-4 py-3 pr-32 font-mono text-sm outline-none resize-y" />
 {mic.supported && (
   <button
     type="button"
     data-testid="track-lyrics-mic-btn"
     onClick={mic.toggle}
     disabled={mic.state === "transcribing"}
     className={`absolute top-2 right-2 font-mono text-[10px] tracking-widest px-3 py-2 border-2 disabled:opacity-50 ${
       mic.state === "recording" ? "border-yt-red text-yt-red animate-pulse-red bg-black" :
       mic.state === "transcribing" ? "border-yt-yellow text-yt-yellow bg-black" :
       "border-yt-yellow text-yt-yellow bg-black hover:bg-yt-yellow hover:text-black"
     }`}
     title="Speak your bars — Whisper transcribes them into the lyrics box"
   >
     {mic.state === "recording" ? "● REC · STOP" :
      mic.state === "transcribing" ? "TRANSCRIBING…" :
      "🎤 SPEAK BARS"}
   </button>
 )}
 </div>
 <div className="flex items-center justify-between mt-1 gap-3 flex-wrap">
   <div data-testid="track-lyrics-mic-status" className="font-mono text-[10px] text-yt-muted flex-1 truncate">
     {mic.status || (mic.supported ? "🎤 Tap SPEAK BARS to dictate — Whisper adds to what's already typed" : "")}
   </div>
   <div className="text-right font-mono text-[10px] text-yt-muted">{lyrics.length}/2000</div>
 </div>
 </Field>

 {/* DJ NEO — songwriter co-pilot. Reads current draft, offers ONE
     specific insight per turn. Collapsible; lives right under the
     lyrics because that's where the conversation is happening. */}
 <DjNeoPanel draft={draftCtx} />

 {/* AUTO-ARRANGE toggle — turns paragraph lyrics into full song structure
     via Claude before Suno renders. This is the "Soniva magic". */}
 <div className="border-2 border-yt-yellow bg-yt-surface p-4 flex items-start gap-3">
   <input
     type="checkbox"
     data-testid="track-auto-arrange"
     id="auto-arrange"
     checked={autoArrange}
     onChange={e => setAutoArrange(e.target.checked)}
     className="mt-1 w-5 h-5 accent-yt-yellow cursor-pointer"
   />
   <label htmlFor="auto-arrange" className="cursor-pointer flex-1">
     <div className="font-display text-lg tracking-widest text-yt-yellow">AUTO-ARRANGE ▶</div>
     <div className="font-mono text-[11px] text-white/70 mt-1 leading-relaxed">
       AI reads your lyrics and turns them into a real song: pulls the hook, groups verses, adds a bridge, structures the flow. Recommended ON.
       <span className="text-yt-muted"> Turn OFF only if you've already written the song with your own [Verse] / [Chorus] tags.</span>
     </div>
   </label>
 </div>

 <Field label="STYLE">
 <PickerGrid opts={STYLE_OPTIONS} value={style} onChange={setStyle} testPrefix="style" />
 </Field>

 <Field label="TEMPO">
 <PickerGrid opts={TEMPO_OPTIONS} value={tempo} onChange={setTempo} testPrefix="tempo" cols="grid-cols-3" />
 </Field>

 <Field label="MOOD">
 <PickerGrid opts={MOOD_OPTIONS} value={mood} onChange={setMood} testPrefix="mood" />
 </Field>

 <Field label="VOICE">
 <PickerGrid opts={VOICE_OPTIONS} value={voice} onChange={setVoice} testPrefix="voice" cols="grid-cols-2 md:grid-cols-3" />
 </Field>

 {/* VIBE — free-text sonic reference. Backend translates it through Claude
     into a Suno-safe descriptor (strips real band names that the filter
     would block). This is how a writer says "Sublime kind of sound" and
     actually gets one, instead of hunting for the closest preset. */}
 <Field label={<>VIBE <span className="text-yt-muted">(optional · describe the sound in your own words)</span></>}>
 <textarea
 data-testid="track-vibe-input"
 value={vibe}
 onChange={e => setVibe(e.target.value)}
 maxLength={400}
 rows={2}
 placeholder="e.g. Sublime porch reggae with plucked acoustic · Kacey Musgraves in a Sunday storm · Kendrick Butterfly-era horns · a Weezer beach drive"
 className="w-full bg-yt-surface border-2 border-yt-border focus:border-yt-yellow px-4 py-3 font-mono text-sm outline-none resize-y"
 />
 <div className="flex items-center justify-between mt-1 gap-3 flex-wrap">
 <div className="font-mono text-[10px] text-yt-muted flex-1">
 Reference any band, artist, era, or feel — I translate it into producer language before it hits Suno, so real names never get filtered out.
 </div>
 <div className="text-right font-mono text-[10px] text-yt-muted">{(vibe || "").length}/400</div>
 </div>
 <UseMyShelfButton onFill={setVibe} />
 </Field>

 {/* EXPLICIT / MATURE toggle — adds an "E" badge and hides the track
     from listeners with the Clean-Only filter on. Honest self-tag. */}
 <div className="border-2 border-yt-red bg-yt-surface p-4 flex items-start gap-3">
   <input
     type="checkbox"
     data-testid="track-explicit"
     id="explicit"
     checked={!!explicit}
     onChange={e => setExplicit(e.target.checked)}
     className="mt-1 w-5 h-5 accent-yt-red cursor-pointer"
   />
   <label htmlFor="explicit" className="cursor-pointer flex-1">
     <div className="font-display text-lg tracking-widest text-yt-red">🅴 EXPLICIT / MATURE</div>
     <div className="font-mono text-[11px] text-white/70 mt-1 leading-relaxed">
       Flag this if the lyrics include swearing, adult themes, violence, or anything you wouldn't play at Sunday dinner.
       <span className="text-yt-muted"> Listeners using the "Clean Only" filter won't see it — but it can still take the crown.</span>
     </div>
   </label>
 </div>

 {showAliasBlock && (
 <Field label={<>PUBLISH AS <span className="text-yt-muted">(optional — leave blank for @{user.rapper_name})</span></>}>
 <input data-testid="track-alias-input"
 value={alias} onChange={e => setAlias(e.target.value)} maxLength={40}
 disabled={anonymous} placeholder={user.rapper_name}
 className="w-full bg-yt-surface border-2 border-yt-border focus:border-yt-yellow px-4 py-3 font-body outline-none disabled:opacity-50" />
 <label className="flex items-center gap-2 mt-3 font-mono text-xs text-yt-muted cursor-pointer">
 <input data-testid="track-anonymous"
 type="checkbox" checked={anonymous} onChange={e => setAnonymous(e.target.checked)}
 className="w-4 h-4 accent-yt-red" />
 Publish anonymously
 </label>
 </Field>
 )}

 {err && <div className="border-l-4 border-yt-red bg-yt-red/10 p-3 font-mono text-sm text-yt-red">{err}</div>}

 <button data-testid={primaryTestId}
 onClick={onSubmit} disabled={busy}
 className="w-full bg-yt-yellow text-black font-display text-2xl tracking-widest px-8 py-5 shadow-brutal hover:bg-white disabled:opacity-50 transition-colors">
 {busy ? "WORKING..." : primaryCta}
 </button>

 <button onClick={onSecondary}
 className="w-full border-2 border-yt-border text-yt-muted font-mono text-xs tracking-widest py-3 hover:border-yt-yellow hover:text-yt-yellow">
 {secondaryCta}
 </button>
 </div>
 </div>
 );
}

function Field({ label, children }) {
 return (
 <div>
 <label className="block font-mono text-[10px] tracking-[0.4em] text-yt-yellow mb-2">{label}</label>
 {children}
 </div>
 );
}

function PickerGrid({ opts, value, onChange, testPrefix, cols = "grid-cols-2 md:grid-cols-5" }) {
 return (
 <div className={`grid ${cols} gap-2`}>
 {opts.map(o => (
 <button
 key={o.v}
 data-testid={`${testPrefix}-${o.v}`}
 onClick={() => onChange(o.v)}
 className={`p-3 border-2 text-left transition-colors ${
 value === o.v
 ? "border-yt-yellow bg-yt-yellow/10"
 : "border-yt-border hover:border-yt-yellow"
 }`}
 >
 <div className={`font-display text-base ${value === o.v ? "text-yt-yellow" : "text-white"}`}>{o.label}</div>
 <div className="font-mono text-[9px] text-yt-muted">{o.sub}</div>
 </button>
 ))}
 </div>
 );
}


/* ============================================================
 DJ NEO — songwriter co-pilot inside the Studio.

 Reads whatever you've got in the form (title / lyrics / style /
 tempo / mood / voice) and gives you ONE clear beat per turn.
 Not a judge. Not a rewriter. A friend at the console.
============================================================ */
function DjNeoPanel({ draft }) {
 const [open, setOpen] = useState(false);
 const [messages, setMessages] = useState([]); // [{role: "user"|"assistant", content}]
 const [input, setInput] = useState("");
 const [busy, setBusy] = useState(false);
 const [err, setErr] = useState("");

 const send = async () => {
  const text = input.trim();
  if (!text || busy) return;
  setErr("");
  const nextMessages = [...messages, { role: "user", content: text }];
  setMessages(nextMessages);
  setInput("");
  setBusy(true);
  try {
   const { data } = await axios.post(`${API}/studio/dj-neo`, {
    message: text,
    history: nextMessages,
    title: draft.title || "",
    lyrics: draft.lyrics || "",
    style: draft.style || "",
    tempo: draft.tempo || "",
    mood: draft.mood || "",
    voice: draft.voice || "",
    vibe: draft.vibe || "",
   });
   setMessages([...nextMessages, { role: "assistant", content: data.reply || "…" }]);
  } catch (e) {
   const detail = e?.response?.data?.detail || "DJ Neo's not answering right now — try again.";
   setErr(detail);
  } finally {
   setBusy(false);
  }
 };

 const onKey = (e) => {
  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
 };

 const quickAsks = [
  { label: "READ MY HOOK", prompt: "Read the lyrics I have and tell me if the hook lands. Quote the line if it does — flag it if it doesn't." },
  { label: "SCRUB SUNO FLAGS", prompt: "Scan my lyrics for anything Suno's moderation filter might flag — brand names, artist names, trigger words. Suggest same-syllable swaps for any hits." },
  { label: "WHERE'S THE BRIDGE?", prompt: "Look at my lyrics and tell me the one place a bridge or beat-switch would carry the song harder." },
  { label: "ONE LINE TO CUT", prompt: "Which single line in my lyrics is the weakest right now, and why? Just one. Be honest." },
 ];

 return (
  <div data-testid="dj-neo-panel" className="border-2 border-yt-yellow bg-yt-surface">
   <button
    type="button"
    data-testid="dj-neo-toggle"
    onClick={() => setOpen(o => !o)}
    className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-yt-yellow/5 transition-colors"
   >
    <div className="flex items-center gap-3 flex-1 min-w-0">
     <div className="w-10 h-10 shrink-0 bg-yt-yellow text-black font-display text-xl flex items-center justify-center">N</div>
     <div className="min-w-0">
      <div className="font-display text-lg tracking-widest text-yt-yellow">DJ NEO</div>
      <div className="font-mono text-[10px] text-white/70 truncate">
       Songwriter co-pilot · reads your bars, suggests hooks, scrubs Suno flags
      </div>
     </div>
    </div>
    <div className="font-mono text-xs text-yt-yellow shrink-0">{open ? "CLOSE ▲" : "OPEN ▼"}</div>
   </button>

   {open && (
    <div data-testid="dj-neo-body" className="border-t-2 border-yt-yellow/50 p-4 space-y-3">
     {/* Conversation area */}
     <div data-testid="dj-neo-messages" className="max-h-80 overflow-y-auto space-y-3 pr-1">
      {messages.length === 0 && (
       <div className="font-mono text-xs text-yt-muted leading-relaxed">
        DJ Neo here, brother. I read whatever you've got in the form — the title, the lyrics, the style you picked — and I give you one clear beat per reply. No lecture, no rewrite. Ask me anything, or tap a quick pick below.
       </div>
      )}
      {messages.map((m, i) => (
       <div
        key={i}
        data-testid={`dj-neo-message-${i}`}
        className={m.role === "user" ? "text-right" : "text-left"}
       >
        <div className={`inline-block max-w-[92%] px-3 py-2 font-mono text-[13px] leading-relaxed whitespace-pre-wrap ${
         m.role === "user"
          ? "bg-yt-yellow text-black"
          : "bg-black border border-yt-yellow/40 text-white/90"
        }`}>
         {m.content}
         {m.role === "assistant" && (
          <DjNeoPlayButton text={m.content} index={i} />
         )}
        </div>
       </div>
      ))}
      {busy && (
       <div className="text-left">
        <div className="inline-block px-3 py-2 font-mono text-[13px] bg-black border border-yt-yellow/40 text-yt-yellow animate-pulse">
         DJ Neo is reading…
        </div>
       </div>
      )}
     </div>

     {/* Quick asks — one-tap prompts so writers with TBI or short attention
         don't have to compose a question every time */}
     <div className="flex flex-wrap gap-2">
      {quickAsks.map(q => (
       <button
        key={q.label}
        type="button"
        data-testid={`dj-neo-quick-${q.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}
        onClick={() => { setInput(q.prompt); }}
        disabled={busy}
        className="font-mono text-[10px] tracking-widest px-2 py-1 border border-yt-yellow/50 text-yt-yellow hover:bg-yt-yellow hover:text-black disabled:opacity-40"
       >
        {q.label}
       </button>
      ))}
     </div>

     {/* Composer */}
     <div className="flex items-stretch gap-2">
      <textarea
       data-testid="dj-neo-input"
       value={input}
       onChange={e => setInput(e.target.value)}
       onKeyDown={onKey}
       rows={2}
       maxLength={1500}
       placeholder="Ask DJ Neo about your track… (Cmd/Ctrl+Enter to send)"
       className="flex-1 bg-black border-2 border-yt-border focus:border-yt-yellow px-3 py-2 font-mono text-sm outline-none resize-y"
      />
      <button
       type="button"
       data-testid="dj-neo-send"
       onClick={send}
       disabled={busy || !input.trim()}
       className="bg-yt-yellow text-black font-display text-sm tracking-widest px-4 disabled:opacity-40 hover:bg-white shrink-0"
      >
       {busy ? "…" : "SEND"}
      </button>
     </div>

     {err && (
      <div data-testid="dj-neo-error" className="border-l-4 border-yt-red bg-yt-red/10 p-2 font-mono text-xs text-yt-red">
       {err}
      </div>
     )}
    </div>
   )}
  </div>
 );
}


/* ============================================================
 UseMyShelfButton — one-tap fill of the VIBE box from the
 writer's Reference Shelf. Merged by Claude on the backend.
============================================================ */
function UseMyShelfButton({ onFill }) {
 const [busy, setBusy] = useState(false);
 const [err, setErr] = useState("");

 const fillFromShelf = async () => {
  if (busy) return;
  setBusy(true); setErr("");
  try {
   const { data } = await axios.get(`${API}/references/merged-vibe`);
   if (!data.vibe) {
    setErr("Shelf is empty — add refs first.");
    return;
   }
   onFill(data.vibe);
  } catch (e) {
   setErr(e?.response?.data?.detail || "Couldn't load the shelf.");
  } finally { setBusy(false); }
 };

 return (
  <div className="mt-2 flex items-center gap-3 flex-wrap">
   <button
    type="button"
    data-testid="use-my-shelf-btn"
    onClick={fillFromShelf}
    disabled={busy}
    className="font-mono text-[11px] tracking-widest px-3 py-1.5 border-2 border-yt-yellow text-yt-yellow hover:bg-yt-yellow hover:text-black disabled:opacity-40 disabled:cursor-not-allowed"
   >
    {busy ? "READING SHELF…" : "▲ USE MY SHELF"}
   </button>
   <Link
    to="/studio/shelf"
    data-testid="open-shelf-link"
    className="font-mono text-[10px] tracking-widest text-yt-muted hover:text-yt-yellow underline"
   >
    manage shelf →
   </Link>
   {err && (
    <span data-testid="use-shelf-error" className="font-mono text-[10px] text-yt-red">{err}</span>
   )}
  </div>
 );
}


/* ============================================================
 DjNeoPlayButton — inline "hear it" button on each assistant reply.
 Paul is audio-first (ADHD + TBI) — reading is heavier than
 listening. One tap turns any DJ Neo message into spoken audio
 via OpenAI TTS (echo voice). Base64 → Blob → HTMLAudioElement.
============================================================ */
function DjNeoPlayButton({ text, index }) {
 const [state, setState] = useState("idle"); // idle | loading | playing | error
 const [audio, setAudio] = useState(null);
 const [err, setErr] = useState("");

 const play = async () => {
  // Already loaded — just replay
  if (audio) {
   try { audio.currentTime = 0; audio.play(); setState("playing"); } catch {}
   return;
  }
  setState("loading"); setErr("");
  try {
   const { data } = await axios.post(`${API}/studio/dj-neo/speak`, {
    text: (text || "").slice(0, 1400),
   });
   // base64 → blob → audio URL
   const bin = atob(data.audio_b64);
   const arr = new Uint8Array(bin.length);
   for (let j = 0; j < bin.length; j++) arr[j] = bin.charCodeAt(j);
   const blob = new Blob([arr], { type: data.mime || "audio/mpeg" });
   const url = URL.createObjectURL(blob);
   // Attach to DOM so the global audio-gate catches this element
   // and pauses any other audible track when it starts (no more
   // DJ Neo talking over a rapper's verse).
   const a = document.createElement("audio");
   a.src = url;
   a.setAttribute("data-audio-role", "dj-neo");
   a.style.display = "none";
   document.body.appendChild(a);
   a.onended = () => { setState("idle"); try { a.remove(); } catch {} try { URL.revokeObjectURL(url); } catch {} };
   a.onerror = () => { setErr("Playback failed."); setState("error"); try { a.remove(); } catch {} };
   setAudio(a);
   setState("playing");
   a.play();
  } catch (e) {
   setErr(e?.response?.data?.detail || "Voice offline — try again.");
   setState("error");
  }
 };

 const stop = () => {
  if (audio) { try { audio.pause(); audio.currentTime = 0; } catch {} }
  setState("idle");
 };

 const label =
  state === "loading" ? "LOADING…" :
  state === "playing" ? "◼ STOP" :
  state === "error" ? "RETRY" :
  "▶ HEAR IT";

 return (
  <div className="mt-2 flex items-center gap-2">
   <button
    type="button"
    data-testid={`dj-neo-play-${index}`}
    onClick={state === "playing" ? stop : play}
    disabled={state === "loading"}
    className="font-mono text-[10px] tracking-widest px-2 py-0.5 border border-yt-yellow/60 text-yt-yellow hover:bg-yt-yellow hover:text-black disabled:opacity-40"
   >
    {label}
   </button>
   {err && (
    <span data-testid={`dj-neo-play-err-${index}`} className="font-mono text-[10px] text-yt-red">{err}</span>
   )}
  </div>
 );
}
